import {
  getGetSuperAdminMarketingPagesSlugQueryKey,
  useGetSuperAdminMarketingPagesSlug,
  usePostSuperAdminMarketingPagesSlugDomain,
  usePostSuperAdminMarketingPagesSlugPublish,
  usePutSuperAdminMarketingPagesSlug,
} from '@agos/api-client/platform'
import { apiErrorMessage, customFetch } from '@agos/api-client'
import type { UploadedMedia } from '@agos/api-client/model'
import { T, formatCount, tt, useModalFocus, useUnsavedWork } from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader, IconButton, ReadFailed, Spinner, StatusBadge } from '@agos/ui'
import { Copy, Trash2, Upload, Video } from 'lucide-react'

import {
  MEDIA_LIBRARY_LIMIT,
  emptyLibraryNote,
  foreignUrlNote,
  itemOfUpload,
  libraryScopeNote,
  mediaRemoveNote,
  readLibrary,
  withUpload,
  withoutUrl,
  writeLibrary,
  type MediaItem,
} from './media-library'

import {
  HERO_TITLE_SIZES,
  KNOWN_SECTIONS,
  styleChoices,
  domainConfirmation,
  domainConfirmed,
  domainProblem,
  domainReport,
  draftNote,
  draftPatch,
  droppedSections,
  draftSaveNote,
  liveDraftOf,
  livePatch,
  liveSaveNote,
  marketingPageOf,
  maySaveLive,
  moved,
  hasUnsavedMarketingWork,
  publishNote,
  publishRefusal,
  publishRefusalMessage,
  removeNote,
  fieldValue,
  flagValue,
  restoreNote,
  sectionEdited,
  listAdded,
  listEdited,
  listEntries,
  listMoved,
  listRemoveNote,
  listRemoved,
  nestedAdded,
  nestedEdited,
  nestedEntries,
  nestedRemoved,
  radiusLabel,
  sectionFields,
  sectionList,
  listValue,
  sectionStyled,
  sectionSummary,
  SITE_FONTS,
  SITE_RADII,
  SITE_THEMES,
  styleWordOf,
  themeLabel,
  withSectionAdded,
  shownSections,
  unknownSectionNote,
  unrenderable,
  withSectionRestored,
  withoutSection,
  type LiveDraft,
  type MarketingSection,
} from './marketing'

/**
 * The marketing page the platform publishes - `SAAS.md` §3.11.
 *
 * **A draft that does not exist is not a draft that differs.** `draftSections` is `null` on this
 * data, and an editor comparing it against the live sections would announce unpublished changes on a
 * page nobody has touched - so the operator publishes to make the warning go away.
 *
 * **FB-505: the publish button had no handler**, and `siteSettings` was read off the wire and thrown
 * away - the object the public site draws `--brand`, `--brand-secondary`, its font and its logo
 * from. So the live brand was settable only by SQL.
 *
 * **And there are two saves, because `save` is asymmetric.** Its own javadoc: title, description and
 * site settings *"do go live immediately; only the sections are staged."* One button reading *Save*
 * would be a false statement about half of what it did.
 */
export function MarketingPage() {
  const { data, isLoading, isError } = useGetSuperAdminMarketingPagesSlug('home')
  const page = useMemo(() => marketingPageOf(data), [data])
  const [viewingDraft, setViewingDraft] = useState(false)

  const queryClient = useQueryClient()
  const refresh = () =>
    void queryClient.invalidateQueries({
      queryKey: getGetSuperAdminMarketingPagesSlugQueryKey('home'),
    })
  /**
   * What the server refused, said out loud - `SAAS.md` §3.65, FB-664.
   *
   * **Neither of these two had an `onError` and nothing rendered `isError` for them**, so a refused
   * save or a refused publish produced nothing at all: no toast, no banner, no change on the page.
   * The operator clicks, the screen does not move, and on `publish` they walk away believing the
   * public site says what they wrote.
   *
   * One sentence for both, cleared on the next success - `BillingPage`'s rule from §3.35: a banner
   * left over from an earlier attempt states something that is no longer true.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const refusedWith = (failure: unknown) => {
    setRefused(apiErrorMessage(failure, tt('Zmenu sa nepodarilo uložiť - server neodpovedal.')))
  }
  const saved = () => {
    setRefused(null)
    // The reordering is cleared **here** and not on the click: `sections` is the only place the
    // operator's order exists, so a refused save that had already discarded it would lose the work
    // and say nothing about it. §3.65.
    setSections(null)
    refresh()
  }

  const save = usePutSuperAdminMarketingPagesSlug({
    mutation: { onSuccess: saved, onError: refusedWith },
  })
  const publish = usePostSuperAdminMarketingPagesSlugPublish({
    mutation: { onSuccess: saved, onError: refusedWith },
  })

  // `null` until somebody types, and then the edited copy. **Derived rather than seeded in an
  // effect**: the page arrives after the first render, and `setState` inside `useEffect` is both a
  // lint error here and an extra render for no reason. Null also lets *Uložiť a zverejniť* tell
  // *untouched* from *changed back*, which a pre-filled copy cannot.
  const [edited, setEdited] = useState<LiveDraft | null>(null)
  // The sections being reordered, or null while nothing has been touched. Null rather than a copy so
  // the *Uložiť do konceptu* button can tell *no change* from *changed back*.
  const [sections, setSections] = useState<MarketingSection[] | null>(null)
  // Which section's content form is open. One at a time - §3.66.
  const [opened, setOpened] = useState<string | null>(null)
  const [adding, setAdding] = useState<string>('hero')
  /**
   * What is waiting for a yes.
   *
   * §3.67 adds a third shape: removing a **list entry** is content leaving the page, so it asks the
   * same way removing a section does. The act is carried in the state rather than re-derived here,
   * because the entry's identity is a list key and an index rather than an id - and re-deriving that
   * after the dialog opened is how the wrong row gets deleted.
   */
  const [asking, setAsking] = useState<
    | 'publish'
    | { remove: MarketingSection }
    | { listRemove: { note: string; act: () => void } }
    | null
  >(null)

  // §3.20, the owner's #22. The slug is fixed for this screen, and it is what `POST …/domain`
  // addresses; the typed-back box is the guard, because the proxy host is provisioned before
  // anything is recorded.
  const slug = 'home'
  const [domain, setDomain] = useState('')
  const [domainAgain, setDomainAgain] = useState('')
  const [askingDomain, setAskingDomain] = useState(false)
  const [domainState, setDomainState] = useState('')
  const attachDomain = usePostSuperAdminMarketingPagesSlugDomain({
    mutation: {
      onSuccess: (answer) => {
        setAskingDomain(false)
        setDomain('')
        setDomainAgain('')
        // Read back rather than assumed: `reused` and `certIssued` are the two facts somebody needs
        // before handing the address to a customer.
        setDomainState(domainReport(answer))
        refresh()
      },
      onError: (failure) => {
        // A 503 is the local stack saying NPM is not configured, and it is the likeliest answer of
        // all here - so it is said plainly rather than left as a dialog that did nothing.
        //
        // **§3.65: that sentence is the fallback now, not a substitute.** It was printed over
        // whatever the server actually said, so a 400 reading *this domain is already in use* was
        // reported as a missing proxy manager. `apiErrorMessage` prefers the server's own sentence
        // and keeps this one for the refusal that carries no envelope, which is the 503 §3.20 was
        // written for.
        setAskingDomain(false)
        setDomainState(
          apiErrorMessage(
            failure,
            tt(
              'Doména sa nenasmerovala - Nginx Proxy Manager nie je v tomto prostredí nakonfigurovaný, alebo ho nebolo možné osloviť.',
            ),
          ),
        )
      },
    },
  })

  /* §9.489, `TODO.md` 3f - both overlays said `aria-modal="true"` and confined nothing. **A pair of refs and
     handlers**, because the hook is element-level precisely so two overlays on one screen cannot both answer
     one Tab, and each is guarded on the writes it can start (§9.480).

     **Above the early return deliberately.** `if (isLoading)` is twenty lines down, and a hook below it is
     called on some renders and not others - which lint catches and which cost this repository a blank screen
     and twenty red e2e tests once already. */
  const domainRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: domainKeyDown } = useModalFocus(
    askingDomain,
    domainRef,
    attachDomain.isPending ? null : () => setAskingDomain(false),
  )
  const confirmRef = useRef<HTMLDivElement>(null)

  /**
   * §3.174, FB-939 - the tab must not take this editing session away silently.
   *
   * **Above the early returns, because it carries a hook.** `isLoading`, `isError` and `page === null` all
   * return below this line, and a `useEffect` after one of them is `react-hooks/rules-of-hooks` -
   * `agos-lint-catches-hooks-after-early-return`, which has cost a blank screen here before. So the flag is
   * computed against a possibly-absent page and the effect reads the flag.
   *
   * **`edited ?? liveDraftOf(page)` is spelled here and again where the editor renders**, and the guard
   * asserts there are exactly two: one expression, two places, so a later change to what *typed* means
   * cannot leave the warning describing a different draft from the one on screen.
   *
   * **`event.preventDefault()` and nothing else**, which is `BurgerBoxPage`'s idiom: every browser has
   * ignored a custom `returnValue` for years, so inventing a sentence would be a claim this cannot keep.
   *
   * **What it does NOT cover is a click on another Tower nav entry** - a client-side route change the
   * browser never sees, one tap away on a phone since §3.173's drawer. The manager does not cover it
   * either, so this closes half the loss and the entry names the other half.
   */
  const unsaved =
    page !== null && hasUnsavedMarketingWork(page, edited ?? liveDraftOf(page), sections)
  useEffect(() => {
    if (!unsaved) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [unsaved])
  /* §3.175, FB-941 - and the sidebar asks too, because `beforeunload` sees a closed tab and a refresh and
     nothing at all of a click on *Franšízy*. The shell reads this through a ref at the moment of the click,
     and the hook clears it on unmount, so a page that leaves while dirty cannot leave the flag standing. */
  useUnsavedWork(unsaved)
  const { onKeyDown: confirmKeyDown } = useModalFocus(
    asking !== null,
    confirmRef,
    publish.isPending || save.isPending ? null : () => setAsking(null),
  )

  /* §3.82 - **three different facts, and this screen stated one of them for all three.**
   * `page` is null while the request is in flight and null when it fails, so the branch below said
   * *Stránka sa nenašla* to an operator whose page exists and is loading, and again to one whose
   * request was refused. A wrong reason is worse than a slow screen: it sends somebody to create a
   * page that is already there. */
  if (isLoading) {
    return (
      <main className="p-4" data-marketing data-marketing-loading>
        <PageHeader title={tt('Marketing')} description={tt('Obsah verejnej stránky platformy.')} />
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      </main>
    )
  }

  if (isError) {
    return (
      <main className="p-4" data-marketing data-marketing-error>
        <PageHeader title={tt('Marketing')} description={tt('Obsah verejnej stránky platformy.')} />
        <ReadFailed><T>Obsah stránky sa nepodarilo načítať.</T></ReadFailed>
      </main>
    )
  }

  if (page === null) {
    return (
      <main className="p-4" data-marketing data-marketing-missing>
        {/* §3.78 - **both** branches carry it. The first pass put one on whichever `<main>` a regex
          * matched first, which left the other bare - and which of the two renders depends on the
          * data, so the e2e caught it on `/marketing` rather than a reader noticing later. */}
        <PageHeader title={tt('Marketing')} description={tt('Obsah verejnej stránky platformy.')} />
        <p className="text-xs text-muted-foreground">
          <T>Stránka sa nenašla.</T>
        </p>
      </main>
    )
  }

  const draft = edited ?? liveDraftOf(page)
  const live = shownSections(page, viewingDraft)
  const editing = sections ?? page.draftSections ?? page.sections
  // §3.15 - what the published page has that the draft no longer does. `editing` reads the draft, so
  // a saved removal put the section out of the editor's reach; the published list still holds it
  // until publish, which is where the way back comes from.
  const dropped = droppedSections(page)
  const refusal = publishRefusal(page)

  return (
    <main className="space-y-3" data-marketing={page.slug}>
      {/* The page's own title is the description here, because that is the thing being edited - the
        * heading says which screen you are on and the subtitle says which page it is editing. */}
      <PageHeader title={tt('Marketing')} description={page.title} />

      {/* §3.65, FB-664 - above everything, because all three writes on this screen share it and the
          one that matters most (*publish*) has no other visible consequence when it is refused. */}
      {refused !== null && (
        <ReadFailed marker="marketing-refused">{refused}</ReadFailed>
      )}

      <div className="rounded-2xl border border-border p-3 space-y-1" data-marketing-state>
        <p className="text-xs" data-draft-note>
          {draftNote(page)}
        </p>
        <p className="text-[11px] text-muted-foreground" data-section-summary>
          {sectionSummary(live)}
        </p>
        {/* The domain. Read from `siteSettings`, and now settable - the owner answered #22 with
          * *"Wire the Tower's domain button"* (§3.20). `POST …/domain` provisions a proxy host on
          * the real Nginx **before** it records anything, so the guard is the domain typed twice. */}
        <p className="text-[11px] text-muted-foreground" data-marketing-domain>
          {page.settings.domain ?? tt('Doména nie je nastavená.')}
        </p>
        <div className="flex flex-wrap items-center gap-2" data-domain-form>
          <input
            data-domain-input
            value={domain}
            maxLength={120}
            placeholder={tt('pizza-majstro.sk')}
            onChange={(event) => setDomain(event.target.value)}
            className="min-h-[32px] flex-1 min-w-[180px] rounded-lg border border-border bg-background px-2 text-xs"
          />
          <button
            type="button"
            data-domain-open
            disabled={domainProblem(domain) !== null || attachDomain.isPending}
            onClick={() => {
              setDomainAgain('')
              setAskingDomain(true)
            }}
            className="min-h-[32px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
          >
            <T>Nasmerovať doménu</T>
          </button>
        </div>
        {domain !== '' && domainProblem(domain) !== null && (
          <p className="text-[11px] text-destructive" data-domain-problem>
            {domainProblem(domain)}
          </p>
        )}
        {domainState !== '' && (
          <p className="text-[11px] text-muted-foreground" data-domain-report>
            {domainState}
          </p>
        )}

        {askingDomain && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={tt('Nasmerovať doménu')}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-domain-confirm
          >
            <div
              ref={domainRef}
              onKeyDown={domainKeyDown}
              className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
            >
              <p className="text-sm" data-domain-confirm-text>
                {domainConfirmation(domain)}
              </p>
              {/* Typed back, the way `FranchiseDetailPage` types a slug to erase a tenant: the fact
                * that has to be right is the one the operator writes out. */}
              <input
                data-domain-confirm-input
                value={domainAgain}
                placeholder={tt('Zopakujte doménu')}
                onChange={(event) => setDomainAgain(event.target.value)}
                className="w-full min-h-[36px] rounded-xl border border-border bg-background px-2 text-xs"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  data-domain-cancel
                  data-dialog-cancel
                  onClick={() => setAskingDomain(false)}
                  className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
                >
                  <T>Zrušiť</T>
                </button>
                <button
                  type="button"
                  data-domain-go
                  disabled={!domainConfirmed(domain, domainAgain) || attachDomain.isPending}
                  onClick={() => {
                    // Re-checked rather than trusted from the disabled button: both boxes stay
                    // editable behind the dialog.
                    if (domainProblem(domain) !== null) return
                    if (!domainConfirmed(domain, domainAgain)) return
                    attachDomain.mutate({ slug, data: { domain: domain.trim().toLowerCase() } })
                  }}
                  className="flex-1 min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
                >
                  <T>Nasmerovať</T>
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-toggle-draft
            disabled={page.draftSections === null}
            onClick={() => setViewingDraft(!viewingDraft)}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
          >
            {viewingDraft ? <T>Zobraziť publikované</T> : <T>Zobraziť koncept</T>}
          </button>
          {/* Offered only when publishing would change something: an always-live button invites a
              click that republishes the same page and moves publishedAt for no reason. */}
          <button
            type="button"
            data-publish
            disabled={refusal !== null || publish.isPending}
            onClick={() => setAsking('publish')}
            className="min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
          >
            <T>Publikovať</T>
          </button>
        </div>
        {/* Why it is off, which a disabled button explains to nobody - and on this data it is off
          * for the server's own reason, *"Žiadny draft - najprv uložte zmeny."* */}
        {refusal !== null && (
          <p className="text-[11px] text-amber-500" data-publish-refusal={refusal}>
            {publishRefusalMessage(refusal)}
          </p>
        )}
      </div>

      {/* ─── The fields that go live on save ────────────────────────────────── */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-live-fields>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Zverejní sa okamžite</T>
          </p>
          <p className="text-[10px] text-amber-500" data-live-note>
            {liveSaveNote()}
          </p>
          <input
            data-field="title"
            value={draft.title}
            onChange={(event) => setEdited({ ...draft, title: event.target.value })}
            placeholder={tt('Názov stránky')}
            className="w-full min-h-[36px] rounded-xl border border-border bg-background px-3 text-xs"
          />
          <textarea
            data-field="description"
            value={draft.description}
            onChange={(event) => setEdited({ ...draft, description: event.target.value })}
            placeholder={tt('Popis pre vyhľadávače')}
            rows={2}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs"
          />
          <div className="flex flex-wrap items-end gap-2">
            {/* Colours as colours. A hex string is not a check anybody can make, and these two are
              * what the public site paints `--brand` and `--brand-secondary` with. */}
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Hlavná farba</T>
              </span>
              <input
                type="color"
                data-field="primaryColor"
                value={draft.primaryColor === '' ? '#000000' : draft.primaryColor}
                onChange={(event) => setEdited({ ...draft, primaryColor: event.target.value })}
                className="block h-9 w-16 rounded-lg border border-border bg-background"
              />
            </label>
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Doplnková farba</T>
              </span>
              <input
                type="color"
                data-field="secondaryColor"
                value={draft.secondaryColor === '' ? '#000000' : draft.secondaryColor}
                onChange={(event) => setEdited({ ...draft, secondaryColor: event.target.value })}
                className="block h-9 w-16 rounded-lg border border-border bg-background"
              />
            </label>
            {/* §3.166 - the theme and the corner radius legacy has offered all along. **An explicit
              * *Predvolená* rather than defaulting to `dark`**: choosing the default and never having chosen
              * are different facts, and only the second leaves the stored blob untouched (`livePatch`'s `set`
              * deletes an empty key). The labels name what the operator sees rather than the stored word. */}
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Vzhľad</T>
              </span>
              <select
                data-field="theme"
                value={draft.theme}
                onChange={(event) => setEdited({ ...draft, theme: event.target.value })}
                className="block min-h-[36px] w-28 rounded-xl border border-border bg-background px-2 text-xs"
              >
                <option value="">{tt('Predvolený')}</option>
                {SITE_THEMES.map((theme) => (
                  <option key={theme} value={theme}>
                    {themeLabel(theme)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Rohy</T>
              </span>
              <select
                data-field="radius"
                value={draft.radius}
                onChange={(event) => setEdited({ ...draft, radius: event.target.value })}
                className="block min-h-[36px] w-28 rounded-xl border border-border bg-background px-2 text-xs"
              >
                <option value="">{tt('Predvolené')}</option>
                {SITE_RADII.map((radius) => (
                  <option key={radius} value={radius}>
                    {radiusLabel(radius)}
                  </option>
                ))}
              </select>
            </label>
            {/* §3.182, FB-956 - **two selects where there was one free-text box.** `cssVariables` writes any
                string into `--font-body`, so typing `Lora` or `Playfair Display` - which are **legacy's**
                families, not AGOS's - saved, round-tripped, and rendered as the system sans with nothing
                saying so. §3.133's `highlight` shape on the page that sells the product. The options are
                `SITE_FONTS`, held to what `apps/marketing/src/index.css` preloads by
                `MarketingFontsAgreeTest`; and the display face is legacy's own second select, which AGOS
                never had, so a heading could not differ from the body. No label map, because a font's name is
                what the operator reads. */}
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Text</T>
              </span>
              <select
                data-field="bodyFont"
                value={draft.bodyFont}
                onChange={(event) => setEdited({ ...draft, bodyFont: event.target.value })}
                className="block min-h-[36px] w-32 rounded-xl border border-border bg-background px-2 text-xs"
              >
                <option value="">{tt('Predvolené')}</option>
                {SITE_FONTS.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px]">
              <span className="text-muted-foreground">
                <T>Nadpisy</T>
              </span>
              <select
                data-field="headingFont"
                value={draft.headingFont}
                onChange={(event) => setEdited({ ...draft, headingFont: event.target.value })}
                className="block min-h-[36px] w-32 rounded-xl border border-border bg-background px-2 text-xs"
              >
                <option value="">{tt('Predvolené')}</option>
                {SITE_FONTS.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </select>
            </label>
            {/* §3.181, FB-954 - this was `data-field="logo"`, and the shopfront drew that value nowhere:
                offered here, sanitised there, read by nothing - legacy's own defect, ported faithfully.
                `faviconUrl` is the field legacy's editor offers and legacy's renderer reads, and §3.180's
                static file is the default it overrides. The placeholder is legacy's own. */}
            <input
              data-field="faviconUrl"
              value={draft.faviconUrl}
              onChange={(event) => setEdited({ ...draft, faviconUrl: event.target.value })}
              placeholder={tt('Ikona v karte prehliadača (URL)')}
              className="min-h-[36px] flex-1 min-w-[10rem] rounded-xl border border-border bg-background px-3 text-xs"
            />
          </div>
          <button
            type="button"
            data-save-live
            disabled={!maySaveLive(page, draft) || save.isPending}
            onClick={() =>
              save.mutate({ slug: page.slug, data: livePatch(page, draft) })
            }
            className="px-3 py-1.5 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
          >
          <T>Uložiť a zverejniť</T>
        </button>
      </section>

      {unknownSectionNote(live) !== '' && (
        <p className="text-[11px] text-amber-500" data-unknown-sections>
          {unknownSectionNote(live)}
        </p>
      )}

      {/* ─── The sections, which are staged ─────────────────────────────────── */}
      <section className="space-y-2" data-section-editor>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Sekcie</T>
        </p>
        <p className="text-[10px] text-muted-foreground" data-draft-save-note>
          {draftSaveNote()}
        </p>
        <ol className="space-y-1" data-marketing-sections>
          {editing.map((section, index) => (
            <li
              key={section.id}
              className="rounded-xl border border-border p-2 text-xs space-y-1.5"
              data-marketing-section={section.type}
              data-section-renderable={unrenderable([section]).length === 0 ? 'yes' : 'no'}
            >
              <div className="flex flex-wrap items-center gap-1.5">
              <span className="flex-1">
                {formatCount(index + 1)}. {section.type}
              </span>
              {/* §3.66 - the content. Collapsed, because nine sections' worth of open forms is a
                * page nobody can find the reorder arrows on. */}
              <button
                type="button"
                data-section-edit={section.id}
                onClick={() => setOpened(opened === section.id ? null : section.id)}
                className="px-2 py-1 rounded-lg border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold"
              >
                {opened === section.id ? <T>Zavrieť</T> : <T>Obsah</T>}
              </button>
              <button
                type="button"
                data-section-up={section.id}
                disabled={index === 0}
                onClick={() => setSections(moved(editing, index, -1))}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                data-section-down={section.id}
                disabled={index === editing.length - 1}
                onClick={() => setSections(moved(editing, index, 1))}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                data-section-remove={section.id}
                onClick={() => setAsking({ remove: section })}
                className="px-2 py-1 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-bold"
              >
                <T>Odobrať</T>
              </button>
              </div>

              {opened === section.id && (
                <div className="space-y-1.5 border-t border-border pt-1.5" data-section-content={section.id}>
                  {sectionFields(section.type).map((field) =>
                    field.kind === 'boolean' ? (
                      <label key={field.key} className="flex items-center gap-2 text-[11px]">
                        <input
                          type="checkbox"
                          data-section-flag={field.key}
                          checked={flagValue(section, field.key)}
                          onChange={(event) =>
                            setSections(
                              sectionEdited(editing, section.id, field.key, event.target.checked),
                            )
                          }
                        />
                        {field.label}
                      </label>
                    ) : (
                      <label key={field.key} className="block space-y-0.5">
                        <span className="text-[10px] text-muted-foreground">{field.label}</span>
                        {/* §3.142 - a select where the server knows the whole set, rather than a text box a
                          * typo looks set in. `choices` is absent on every other kind, so the fall-through
                          * below is unchanged for them. */}
                        {field.kind === 'choice' && field.choices !== undefined ? (
                          <select
                            data-section-choice={field.key}
                            value={fieldValue(section, field.key) || field.choices[0]?.value}
                            onChange={(event) =>
                              setSections(
                                sectionEdited(editing, section.id, field.key, event.target.value),
                              )
                            }
                            className="w-full min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                          >
                            {field.choices.map((choice) => (
                              <option key={choice.value} value={choice.value}>
                                {choice.label}
                              </option>
                            ))}
                          </select>
                        ) : field.kind === 'multiline' ? (
                          <textarea
                            data-section-field={field.key}
                            rows={2}
                            value={fieldValue(section, field.key)}
                            onChange={(event) =>
                              setSections(
                                sectionEdited(editing, section.id, field.key, event.target.value),
                              )
                            }
                            className="w-full rounded-xl border border-border bg-background px-2 py-1 text-xs"
                          />
                        ) : (
                          <input
                            data-section-field={field.key}
                            value={fieldValue(section, field.key)}
                            onChange={(event) =>
                              setSections(
                                sectionEdited(editing, section.id, field.key, event.target.value),
                              )
                            }
                            className="w-full min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                          />
                        )}
                      </label>
                    ),
                  )}

                  <label className="block space-y-0.5">
                    {/* Read by the site as a slug, so a hero button can scroll here. */}
                    <span className="text-[10px] text-muted-foreground">
                      <T>Odkaz na sekciu (anchor)</T>
                    </span>
                    <input
                      data-section-field="anchorId"
                      value={fieldValue(section, 'anchorId')}
                      onChange={(event) =>
                        setSections(
                          sectionEdited(editing, section.id, 'anchorId', event.target.value),
                        )
                      }
                      className="w-full min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                    />
                  </label>

                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(styleChoices(section.type, section)).map(([key, choices]) => (
                      <label key={key} className="space-y-0.5">
                        <span className="text-[10px] text-muted-foreground">{key}</span>
                        <select
                          data-section-style={key}
                          value={styleWordOf(section, key)}
                          onChange={(event) =>
                            setSections(
                              sectionStyled(editing, section.id, key, event.target.value),
                            )
                          }
                          className="min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                        >
                          {choices.map((choice) => (
                            <option key={choice} value={choice}>
                              {choice === '' ? tt('(zdedené)') : choice}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                    {section.type === 'hero' && (
                      <label className="space-y-0.5">
                        {/* Legacy takes `titleSize` for the hero only; every other section
                          * hard-codes its scale, so offering it there would draw nothing. */}
                        <span className="text-[10px] text-muted-foreground">titleSize</span>
                        <select
                          data-section-style="titleSize"
                          value={styleWordOf(section, 'titleSize')}
                          onChange={(event) =>
                            setSections(
                              sectionStyled(editing, section.id, 'titleSize', event.target.value),
                            )
                          }
                          className="min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                        >
                          {HERO_TITLE_SIZES.map((choice) => (
                            <option key={choice} value={choice}>
                              {choice === '' ? tt('(zdedené)') : choice}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>

                  {/* §3.67, FB-686 - **the list, which is most of what these types say.** §3.66
                    * built the scalars and named this omission; six of the nine types have no list
                    * and draw nothing here. */}
                  <SectionListEditor
                    section={section}
                    editing={editing}
                    setSections={setSections}
                    ask={(note, act) => setAsking({ listRemove: { note, act } })}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
        {/* §3.15 - the way back from a removal. `Odobrať` was the only destructive act on this
          * screen with no exit once saved, and publishing would have made it permanent. */}
        {dropped.length > 0 && (
          <div className="rounded-xl border border-amber-500/40 p-2 space-y-1" data-dropped-sections>
            <p className="text-[10px] text-amber-500">
              <T>Tieto sekcie sú na publikovanej stránke, ale nie v koncepte - po publikovaní zmiznú.</T>
            </p>
            {dropped.map((section) => (
              <div key={section.id} className="flex items-center gap-1.5 text-xs">
                <span className="flex-1" data-dropped-section={section.type}>
                  {section.type}
                </span>
                <button
                  type="button"
                  data-restore-section={section.id}
                  title={restoreNote(section)}
                  onClick={() => setSections(withSectionRestored(page, editing, section.id))}
                  className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                >
                  <T>Vrátiť do konceptu</T>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* §3.66 - a page could be reordered and emptied and never added to. Only a type the site
          * can draw, because a section that saves and never appears is the silent direction. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <select
            data-add-section-type
            value={adding}
            onChange={(event) => setAdding(event.target.value)}
            className="min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
          >
            {KNOWN_SECTIONS.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-add-section
            onClick={() => setSections(withSectionAdded(editing, adding, crypto.randomUUID()))}
            className="px-2 py-1 rounded-lg border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold"
          >
            <T>Pridať sekciu</T>
          </button>
        </div>

        <MediaLibrary />

        {/* §3.174 - what an operator sees before they reach for the tab. The `beforeunload` catches them
          * when they do not; a guard that only fires on a close leaves somebody unaware until it is too
          * late to act, which is §9.557's argument about a warning the paper does not carry. */}
        <div className="flex flex-wrap items-center gap-2">
          {unsaved && (
            <span
              className="text-xs font-semibold text-amber-500 whitespace-nowrap"
              data-marketing-unsaved
            >
              <T>Neuložené zmeny</T>
            </span>
          )}
          <button
            type="button"
            data-save-draft
            disabled={sections === null || save.isPending}
            // §3.65 - no `setSections(null)` here. `sections` is the reordering itself, and clearing
            // it on the click meant a refused save lost the work as well as saying nothing. It is
            // cleared in `saved`, when the server has agreed.
            onClick={() => save.mutate({ slug: page.slug, data: draftPatch(editing) })}
            className="px-3 py-1.5 rounded-xl border border-border text-xs font-bold disabled:opacity-40"
          >
            <T>Uložiť do konceptu</T>
          </button>
        </div>
      </section>

      {asking !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={asking === 'publish' ? tt('Publikovať') : tt('Odobrať')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-marketing-confirm={
            asking === 'publish' ? 'publish' : 'listRemove' in asking ? 'list-remove' : 'remove'
          }
        >
          <div
            ref={confirmRef}
            onKeyDown={confirmKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
          >
            <p className="text-sm" data-marketing-confirm-text>
              {asking === 'publish'
                ? publishNote(page)
                : 'listRemove' in asking
                  ? asking.listRemove.note
                  : removeNote(asking.remove)}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-marketing-cancel
                data-dialog-cancel
                onClick={() => setAsking(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-marketing-confirm-go
                disabled={publish.isPending || save.isPending}
                onClick={() => {
                  if (asking === 'publish') publish.mutate({ slug: page.slug })
                  // §3.67 - the act was decided when the dialog opened; see the state's own note.
                  else if ('listRemove' in asking) asking.listRemove.act()
                  // Removing only stages: the section leaves the draft, and the page keeps it until
                  // somebody publishes. `removeNote` says exactly that before the click.
                  else setSections(withoutSection(editing, asking.remove.id))
                  setAsking(null)
                }}
                className="flex-1 min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                {asking === 'publish' ? <T>Publikovať</T> : <T>Odobrať</T>}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

/**
 * A section's list, edited - `SAAS.md` §3.67, FB-686.
 *
 * **Its own component because the footer nests**: a column holds `links`, so the same four controls
 * appear one level down, and inlining that in the page would have meant writing them twice. The spec
 * carries the nesting as data (`sectionList(type).nested`), so a pricing tier's own feature list is
 * a spec change rather than another component.
 *
 * Six of the nine renderable types have no list and draw nothing here.
 */
function SectionListEditor({
  section,
  editing,
  setSections,
  ask,
}: {
  section: MarketingSection
  editing: MarketingSection[]
  setSections: (sections: MarketingSection[]) => void
  ask: (note: string, act: () => void) => void
}) {
  const spec = sectionList(section.type)
  if (spec === null) return null
  const entries = listEntries(section, spec.key)

  return (
    <div className="space-y-1.5 border-t border-border pt-1.5" data-section-list={spec.key}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {spec.label}
      </p>

      {entries.map((entry, index) => (
        <div
          key={index}
          className="rounded-xl border border-border p-1.5 space-y-1"
          data-list-entry={index}
        >
          <div className="flex items-center gap-1">
            <span className="flex-1 text-[10px] text-muted-foreground">
              {spec.entryLabel} {index + 1}
            </span>
            <button
              type="button"
              data-entry-up={index}
              disabled={index === 0}
              onClick={() => setSections(listMoved(editing, section.id, spec.key, index, -1))}
              className="px-1.5 py-0.5 rounded-lg border border-border text-[10px] disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              data-entry-down={index}
              disabled={index === entries.length - 1}
              onClick={() => setSections(listMoved(editing, section.id, spec.key, index, 1))}
              className="px-1.5 py-0.5 rounded-lg border border-border text-[10px] disabled:opacity-30"
            >
              ↓
            </button>
            {/* Content somebody typed, so it asks - the arrows and the add button do not. */}
            <button
              type="button"
              data-entry-remove={index}
              onClick={() =>
                ask(listRemoveNote(spec, index), () =>
                  setSections(listRemoved(editing, section.id, spec.key, index)),
                )
              }
              className="px-1.5 py-0.5 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-bold"
            >
              <T>Odobrať</T>
            </button>
          </div>

          {spec.fields.map((field) => (
            <label key={field.key} className="block space-y-0.5">
              <span className="text-[10px] text-muted-foreground">{field.label}</span>
              {field.kind === 'multiline' ? (
                <textarea
                  data-entry-field={`${String(index)}.${field.key}`}
                  rows={2}
                  value={listValue(entry, field.key)}
                  onChange={(event) =>
                    setSections(
                      listEdited(
                        editing,
                        section.id,
                        spec.key,
                        index,
                        field.key,
                        event.target.value,
                      ),
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-2 py-1 text-xs"
                />
              ) : (
                <input
                  data-entry-field={`${String(index)}.${field.key}`}
                  value={listValue(entry, field.key)}
                  onChange={(event) =>
                    setSections(
                      listEdited(
                        editing,
                        section.id,
                        spec.key,
                        index,
                        field.key,
                        event.target.value,
                      ),
                    )
                  }
                  className="w-full min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                />
              )}
            </label>
          ))}

          {spec.nested !== null && (
            <div className="space-y-1 border-t border-border pt-1" data-nested-list={spec.nested.key}>
              <p className="text-[10px] text-muted-foreground">{spec.nested.label}</p>
              {nestedEntries(entry, spec.nested.key).map((nested, nestedIndex) => (
                <div key={nestedIndex} className="flex flex-wrap items-end gap-1" data-nested-entry={nestedIndex}>
                  {spec.nested?.fields.map((field) => (
                    <label key={field.key} className="space-y-0.5">
                      <span className="text-[10px] text-muted-foreground">{field.label}</span>
                      <input
                        data-nested-field={`${String(index)}.${String(nestedIndex)}.${field.key}`}
                        value={listValue(nested, field.key)}
                        onChange={(event) =>
                          setSections(
                            nestedEdited(
                              editing,
                              section.id,
                              spec.key,
                              index,
                              spec.nested?.key ?? '',
                              nestedIndex,
                              field.key,
                              event.target.value,
                            ),
                          )
                        }
                        className="min-h-[32px] rounded-xl border border-border bg-background px-2 text-xs"
                      />
                    </label>
                  ))}
                  <button
                    type="button"
                    data-nested-remove={`${String(index)}.${String(nestedIndex)}`}
                    onClick={() =>
                      ask(spec.nested?.entryLabel ?? '', () =>
                        setSections(
                          nestedRemoved(
                            editing,
                            section.id,
                            spec.key,
                            index,
                            spec.nested?.key ?? '',
                            nestedIndex,
                          ),
                        ),
                      )
                    }
                    className="px-1.5 py-1 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-bold"
                  >
                    <T>Odobrať</T>
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-nested-add={index}
                onClick={() =>
                  setSections(
                    nestedAdded(editing, section.id, spec.key, index, spec.nested?.key ?? ''),
                  )
                }
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
              >
                + {spec.nested.entryLabel}
              </button>
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        data-entry-add={spec.key}
        onClick={() => setSections(listAdded(editing, section.id, spec.key))}
        className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
      >
        + {spec.entryLabel}
      </button>
    </div>
  )
}

/**
 * Knižnica médií - `SAAS.md §3.101`.
 *
 * **`POST /api/v1/upload-media` was built for this panel and had no caller.** Its javadoc says
 * *"Upload anything the marketing library takes… The Control Tower token is checked here rather than
 * left to the ordinary guard, exactly as legacy does - it is the one endpoint both authorities
 * share."* A whole auth path added to one endpoint for the Tower, and the screen it was added for did
 * not exist. Every section that shows a picture takes an image **URL as a text field**, so without
 * this a photograph goes up somewhere else and comes back as a pasted link.
 *
 * Its own component because it owns its own state and touches nothing else on the page: the editor
 * above it is a draft of sections, and this is a clipboard.
 */
function MediaLibrary() {
  const [items, setItems] = useState<MediaItem[]>(() => readLibrary())
  const [uploading, setUploading] = useState(false)
  const [problem, setProblem] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const keep = (next: MediaItem[]) => {
    setItems(next)
    writeLibrary(next)
  }

  const upload = async (file: File) => {
    setUploading(true)
    setProblem('')
    try {
      // **A real `FormData`, not the generated client.** `postUploadMedia` sends
      // `JSON.stringify(body)` with `Content-Type: application/json` against a multipart endpoint,
      // because orval types a file part as a `Blob` field. Every other upload in this repository
      // goes through `customFetch` with a form for the same reason.
      const form = new FormData()
      form.append('file', file)
      // **The generated type, not a hand-written shape.** §9.416's ratchet refuses
      // `customFetch<MyOwnShape>` where the server declares one, and it is right: a hand-declared
      // type that agrees today is one rename away from a green build over a response that no longer
      // carries the field. `itemOfUpload`'s parameter stays loose on purpose - `UploadedMedia` says
      // `url: string`, and a type is a claim rather than a guarantee
      // (`agos-empty-array-cannot-contradict-a-type`), so the empty-url case is still checked.
      // `Partial<>` for the same reason `ProductDialog`'s upload uses it: the declared shape is
      // what the server means to send, not a promise about the one 200 that goes wrong.
      const response = await customFetch<Partial<UploadedMedia>>('/api/v1/upload-media', {
        method: 'POST',
        body: form,
      })
      const added = itemOfUpload(response, file.name, Date.now())
      if (added === null) {
        // The one failure a 200 can still carry, which legacy checks for too.
        setProblem(tt('Server nevrátil adresu súboru.'))
        return
      }
      keep(withUpload(items, added))
    } catch (caught) {
      // Legacy's is an `alert`. §10A.200 is why that matters: the owner's own upload report was
      // *"There is no error, nothing"*, and the fix was the server naming the directory it could
      // not write - a sentence an alert takes out of the flow and a line under the control keeps.
      setProblem(apiErrorMessage(caught, tt('Nahrávanie zlyhalo.')))
    } finally {
      setUploading(false)
    }
  }

  const copy = async (url: string) => {
    try {
      // **Copied as it came.** Legacy resolves it against `window.location.origin` first, which in
      // AGOS would paste the *admin* domain into a section of the public site - see `isOwnUpload`.
      await navigator.clipboard.writeText(url)
      setCopied(url)
      window.setTimeout(() => setCopied(null), 1500)
    } catch {
      // No clipboard permission. The URL is already on screen and selectable, so say that rather
      // than opening a `prompt` the way legacy does.
      setProblem(tt('Prehliadač nedovolil kopírovanie - adresu označte a skopírujte ručne.'))
    }
  }

  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-media-library={String(items.length)}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Knižnica médií</T>
        </p>
        <label className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-[11px] font-bold cursor-pointer">
          {uploading ? <Spinner /> : <Upload className="w-3 h-3" />}
          <T>Nahrať</T>
          <input
            type="file"
            data-media-upload
            accept="image/*,video/*"
            className="hidden"
            disabled={uploading}
            onChange={(change) => {
              const file = change.target.files?.[0]
              // Cleared so choosing the same file twice fires again, which is what an operator who
              // has just fixed the picture does.
              change.target.value = ''
              if (file !== undefined) void upload(file)
            }}
          />
        </label>
      </div>

      <p className="text-[11px] text-muted-foreground" data-media-scope>
        {libraryScopeNote()}
      </p>

      {problem !== '' && (
        <p className="text-[11px] font-semibold text-destructive" data-media-problem>
          {problem}
        </p>
      )}

      {items.length === 0 ? (
        <p className="text-[11px] text-muted-foreground" data-media-empty>
          {emptyLibraryNote()}
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li
              key={item.url}
              className="flex items-center gap-2 rounded-lg border border-border p-1.5"
              data-media-item={item.url}
            >
              <div className="w-10 h-10 shrink-0 overflow-hidden rounded bg-muted flex items-center justify-center">
                {item.kind === 'video' ? (
                  <Video className="w-4 h-4 text-violet-500" />
                ) : (
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-medium" title={item.name}>
                  {item.name}
                </p>
                <p className="truncate font-mono text-[10px] text-muted-foreground">{item.url}</p>
                {foreignUrlNote(item.url) !== '' && (
                  <p className="text-[10px] text-amber-500" data-media-foreign>
                    {foreignUrlNote(item.url)}
                  </p>
                )}
              </div>
              {copied === item.url && <StatusBadge label={tt('skopírované')} color="green" />}
              {/* §3.118 - the product's own row-action button, which these two were copies of: their
                * `p-1.5 rounded-lg border border-border text-muted-foreground` is `iconButtonClass()`'s
                * base plus its own neutral tone, character for character. */}
              <IconButton
                label={tt('Kopírovať adresu')}
                onClick={() => void copy(item.url)}
                attributes={{ 'data-media-copy': item.url }}
              >
                <Copy className="w-3.5 h-3.5" />
              </IconButton>
              <IconButton
                label={mediaRemoveNote()}
                onClick={() => keep(withoutUrl(items, item.url))}
                attributes={{ 'data-media-remove': item.url }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {items.length >= MEDIA_LIBRARY_LIMIT && (
        <p className="text-[10px] text-muted-foreground" data-media-capped>
          <T>Zoznam je plný - najstaršie záznamy sa vyradia.</T>
        </p>
      )}
    </div>
  )
}
